<template>

  <Modal v-if="visible" :value="true" @input="closeModal">
    <span slot="header">{{ blocked ? 'Não é possível excluir este motorista' : title }}</span>

    <div class="row text-align-center">
      <div class="col-md-12">
        <p v-if="loading" class="mb-3">Verificando vínculos...</p>

        <div v-else-if="loadFailed" class="alert alert-danger text-left mb-3">
          Não foi possível verificar os registros vinculados a este motorista. Tente novamente em
          instantes. A exclusão fica indisponível até a verificação concluir.
        </div>

        <template v-else>
          <p v-if="blocked" class="mb-3">
            O motorista <strong>{{ driver.name }}</strong> tem
            {{ blockingIndications.length === 1 ? 'uma indicação de condutor em andamento' : `${blockingIndications.length} indicações de condutor em andamento` }}.
          </p>
          <p v-else class="mb-3">Tem certeza que deseja excluir o motorista <strong>{{ driver.name }}</strong> ?</p>

          <div v-if="blocked" class="alert alert-danger text-left mb-3">
            <p class="mb-2">
              A indicação de condutor tem prazo legal para ser concluída. Excluir o cadastro agora
              impediria a conclusão {{ blockingIndications.length === 1 ? 'do processo abaixo' : 'dos processos abaixo' }}.
              <strong>Nenhum dado será alterado.</strong>
            </p>
            <ul class="mb-2">
              <li v-for="indication in blockingIndications" :key="indication.protocol || indication.ait">
                <strong>{{ indication.protocol || '-' }}</strong>
                <span v-if="indication.ait"> · AIT {{ indication.ait }}</span>
                <br>
                Situação: {{ indication.statusName }}
                <span v-if="indication.deadline"> · Prazo: {{ indication.deadline }}</span>
              </li>
            </ul>
            <p class="mb-0">
              A exclusão fica liberada assim que
              {{ blockingIndications.length === 1 ? 'o processo for encerrado' : 'todos forem encerrados' }}.
              <a :href="indicationsLink" target="_blank" class="link-tickets">Clique para ver as indicações</a>
            </p>
          </div>

          <div v-else-if="hasLinks" class="alert alert-warning text-left mb-3">
            <p class="mb-2">
              <strong>Atenção:</strong> este motorista tem registros vinculados. Eles
              <strong>não serão excluídos</strong>: sai apenas a identificação. As colunas CPF, Nome
              e Categoria passam a exibir <strong>"Motorista excluído"</strong> nas três.
            </p>
            <ul class="mb-2">
              <li v-if="ticketsCount > 0">
                <strong>{{ ticketsCount }}</strong> {{ ticketsCount === 1 ? 'multa' : 'multas' }} no CaçaMultas Frotas
                <a :href="ticketsLink" target="_blank" class="link-tickets">Clique para ver os AITs (Autos de Infração) Vinculados</a>
              </li>
              <li v-if="endedIndicationsCount > 0">
                <strong>{{ endedIndicationsCount }}</strong>
                {{ endedIndicationsCount === 1 ? 'indicação encerrada' : 'indicações encerradas' }} na Indicação de Condutores
                <a :href="indicationsLink" target="_blank" class="link-tickets">Clique para ver as indicações</a>
              </li>
            </ul>
            <p class="mb-0">
              AIT, placa, valor e data continuam disponíveis. Depois da exclusão, esses registros
              podem ser encontrados filtrando o motorista por "Motorista excluído" nos dois produtos.
            </p>
          </div>

          <p v-if="!blocked" class="mb-3">
            O CPF <strong>{{ driver.cpf | cpf }}</strong> deixará de existir na base. Ele permanece
            apenas no Histórico de Ações, como comprovação de quem excluiu e quando. Esta ação não
            pode ser desfeita.
          </p>
        </template>

        <button
          v-if="canConfirm"
          class="btn btn-outline-red btn-md mr-2"
          @click="$emit('sendDeleteModel', driver)"
        >
          Excluir
        </button>
        <button class="btn btn-outline btn-md" @click="closeModal">{{ blocked ? 'Fechar' : 'Cancelar' }}</button>
      </div>
    </div>
  </Modal>

</template>

<script>
  import Modal from '../shares/specialTag/Modal'

  export default {
    components: { Modal },
    computed: {
      visible() {
        return this.driver && this.value
      },
      blocked() {
        return this.blockingIndications.length > 0
      },
      hasLinks() {
        return this.ticketsCount > 0 || this.endedIndicationsCount > 0
      },
      canConfirm() {
        return !this.loading && !this.loadFailed && !this.blocked
      },
      cpf() {
        if (!this.driver || !this.driver.cpf) return ''
        return this.driver.cpf.replace(/\D/g, '')
      },
      ticketsLink() {
        return `/cm-frotas?q[cpf]=${this.cpf}`
      },
      indicationsLink() {
        return `/indicacao-condutores?q[cpf]=${this.cpf}`
      }
    },
    data: () => ({
      ticketsCount: 0,
      endedIndicationsCount: 0,
      blockingIndications: [],
      loading: false,
      loadFailed: false,
      lastLoadedId: null
    }),
    methods: {
      closeModal() {
        this.$emit('input', false)
        this.resetSummary()
        this.lastLoadedId = null
      },
      resetSummary() {
        this.ticketsCount = 0
        this.endedIndicationsCount = 0
        this.blockingIndications = []
        this.loadFailed = false
      },
      loadSummary() {
        if (!this.driver || !this.driver.id) {
          this.resetSummary()
          return
        }

        if (this.lastLoadedId === this.driver.id) {
          return
        }

        this.loading = true
        this.loadFailed = false
        this.lastLoadedId = this.driver.id

        this.axios.get(`drivers/${this.driver.id}/deletion_summary`)
          .then(response => {
            const data = response.data || {}
            this.ticketsCount = data.tickets || 0
            this.endedIndicationsCount = data.ended_indications || data.endedIndications || 0
            this.blockingIndications = (data.blocking_indications || data.blockingIndications || []).map(i => ({
              protocol: i.protocol,
              ait: i.ait,
              statusName: i.status_name || i.statusName,
              deadline: i.indication_deadline || i.indicationDeadline
            }))
          })
          .catch(e => {
            this.$notify.addError(e)
            this.resetSummary()
            this.loadFailed = true
          })
          .finally(() => {
            this.loading = false
          })
      }
    },
    name: 'ModalDeleteDriver',
    props: {
      title: { default: 'Excluir motorista', type: String },
      value: { default: false, type: Boolean },
      driver: { type: Object },
    },
    watch: {
      value: {
        immediate: true,
        handler(newValue) {
          if (newValue && this.driver) {
            this.loadSummary()
          }
        }
      },
      driver: {
        immediate: true,
        handler(newValue) {
          if (newValue && this.value) {
            this.loadSummary()
          }
        }
      }
    }
  }
</script>

<style scoped>
.alert-warning {
  background-color: var(--alert-warning-bg) !important;
  border-color: var(--alert-warning-border) !important;
  color: var(--alert-warning-text) !important;
}

.alert-warning p,
.alert-warning li,
.alert-warning strong {
  color: var(--alert-warning-text) !important;
}

.alert-warning ul,
.alert-danger ul {
  padding-left: 20px;
  margin-bottom: 0;
}

.alert-warning li,
.alert-danger li {
  margin-bottom: 4px;
}

.link-tickets {
  color: var(--primary-color) !important;
  text-decoration: underline;
  font-weight: 600;
}

.link-tickets:hover {
  color: var(--primary-color-hover) !important;
}
</style>
